import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const routes: RouteRecordRaw[] = [
  { path: '/', name: 'today', component: () => import('@/views/TodayView.vue') },
  { path: '/week', name: 'week', component: () => import('@/views/WeekView.vue') },
  { path: '/plan', name: 'plan', component: () => import('@/views/PlanWeekView.vue') },
  { path: '/training', name: 'training', component: () => import('@/views/TrainingView.vue') },
  { path: '/review', name: 'review', component: () => import('@/views/ReviewView.vue') },
  { path: '/cook/:id', name: 'cook', component: () => import('@/views/CookView.vue') },
  { path: '/people', name: 'people', component: () => import('@/views/MembersView.vue') },
  { path: '/people/:id/stats', name: 'stats', component: () => import('@/views/StatsView.vue') },
  {
    path: '/people/:id/stats/:metric',
    name: 'stat-detail',
    component: () => import('@/views/StatDetailView.vue'),
  },
  { path: '/check-in', name: 'check-in', component: () => import('@/views/CheckinView.vue') },
  { path: '/measurements', name: 'measurements', component: () => import('@/views/BodyView.vue') },
  { path: '/about', name: 'about', component: () => import('@/views/HomeView.vue') },
  { path: '/foods', name: 'foods', component: () => import('@/views/FoodsView.vue') },
  {
    path: '/capture',
    name: 'capture',
    component: () => import('@/views/CaptureProductView.vue'),
  },
  {
    path: '/foods/:id/check',
    name: 'food-check',
    component: () => import('@/views/CheckProductView.vue'),
  },
  { path: '/inventory', name: 'inventory', component: () => import('@/views/InventoryView.vue') },
  { path: '/use-soon', name: 'use-soon', component: () => import('@/views/UseSoonView.vue') },
  { path: '/scan', name: 'scan', component: () => import('@/views/ScanView.vue') },
  { path: '/label', name: 'label', component: () => import('@/views/LabelView.vue') },
  // Opens from what the phone kept when there is no signal (N-06).
  { path: '/shopping', name: 'shopping', component: () => import('@/views/ShoppingView.vue'), meta: { offline: true } },
  { path: '/recipes', name: 'recipes', component: () => import('@/views/RecipesView.vue') },
  {
    path: '/recipes/new',
    name: 'recipe-new',
    component: () => import('@/views/RecipeEditView.vue'),
  },
  { path: '/imports', name: 'imports', component: () => import('@/views/ImportsView.vue') },
  {
    path: '/recipes/import',
    name: 'recipe-import',
    component: () => import('@/views/RecipeImportView.vue'),
  },
  {
    path: '/recipes/:id',
    name: 'recipe-detail',
    component: () => import('@/views/RecipeDetailView.vue'),
  },
  { path: '/books', name: 'books', component: () => import('@/views/BooksView.vue') },
  {
    path: '/books/:id',
    name: 'book-detail',
    component: () => import('@/views/BookDetailView.vue'),
  },
  {
    path: '/recipes/:id/edit',
    name: 'recipe-edit',
    component: () => import('@/views/RecipeEditView.vue'),
  },
  { path: '/household', name: 'household', component: () => import('@/views/HouseholdView.vue') },
  { path: '/settings', name: 'settings', component: () => import('@/views/SettingsView.vue') },
  { path: '/admin/foods', name: 'admin-foods', component: () => import('@/views/AdminFoodsView.vue') },
  {
    path: '/admin/foods/review',
    name: 'admin-food-review',
    component: () => import('@/views/AdminFoodReviewView.vue'),
  },
  { path: '/admin/play', name: 'admin-play', component: () => import('@/views/AdminPlayView.vue') },
  { path: '/admin/restaurants', name: 'admin-restaurants', component: () => import('@/views/AdminRestaurantsView.vue') },
  {
    path: '/admin/restaurants/:id',
    name: 'admin-restaurant-import',
    component: () => import('@/views/AdminRestaurantImportView.vue'),
  },
  {
    path: '/admin/foods/:id',
    name: 'admin-food',
    component: () => import('@/views/AdminFoodEditView.vue'),
  },
  {
    path: '/connections',
    name: 'connections',
    component: () => import('@/views/ConnectionsView.vue'),
  },
  {
    path: '/connections/:provider/callback',
    name: 'connection-callback',
    component: () => import('@/views/ConnectionCallbackView.vue'),
  },
  {
    path: '/everyone',
    name: 'everyone',
    component: () => import('@/views/HouseholdDayView.vue'),
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: () => import('@/views/NotificationsView.vue'),
  },
  {
    path: '/auth/google/done',
    name: 'google-done',
    component: () => import('@/views/GoogleDoneView.vue'),
    meta: { public: true },
  },
  {
    // An MCP client asking to act as the signed-in person (ADR-040). Needs a
    // sign-in, so a stranger is sent to sign in first and comes back here.
    path: '/oauth/authorize',
    name: 'oauth-authorize',
    component: () => import('@/views/OAuthAuthorizeView.vue'),
    meta: { bare: true },
  },
  {
    path: '/sign-in',
    name: 'sign-in',
    component: () => import('@/views/SignInView.vue'),
    meta: { public: true },
  },
  {
    path: '/sign-up',
    name: 'sign-up',
    component: () => import('@/views/SignUpView.vue'),
    meta: { public: true },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: { public: true },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: { public: true },
  },
  {
    path: '/verify',
    name: 'verify',
    component: () => import('@/views/VerifyView.vue'),
    meta: { public: true },
  },
  {
    path: '/accept-invite',
    name: 'accept-invite',
    component: () => import('@/views/AcceptInviteView.vue'),
    meta: { public: true },
  },
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('@/views/NotFound.vue') },
]

export const router = createRouter({ history: createWebHistory(), routes })

let restored = false

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  if (!restored) {
    restored = true
    await auth.restore()
  }
  if (to.meta.public) return true
  // Still signed in but out of signal: a page that works from the device's
  // copy opens anyway rather than waiting at the sign-in page.
  if (!auth.isSignedIn && auth.unreachable && to.meta.offline) return true
  if (!auth.isSignedIn) return { name: 'sign-in', query: { next: to.fullPath } }
  return true
})
